/* eslint-disable no-nested-ternary, no-restricted-syntax, func-call-spacing, no-spaced-func */
import React, { useState, useEffect, useCallback, useRef } from 'react';

// React Virtuoso
import { TableVirtuoso } from 'react-virtuoso/dist/index';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Draggable Scroll
import { useDraggable } from 'react-use-draggable-scroll';

// Material
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';

// Helpers
import { Order, stableSort, getComparator } from 'helpers/tableSortLabels';
import { Capitalize } from 'helpers/capitalize';
import { currencyFormat } from 'helpers/currencyFormat';
import { maskCPF } from 'helpers/maskers';

// Constants
import CONST_TOKEN from 'constants/token';
import CONST_SELLING_POINTS, {
  CONST_SELLING_POINTS_OBJECT,
} from 'constants/sellingPoints';
import CONST_TRANSPORT_VOUCHER_COLLECTION from 'constants/transportVoucherCollectionReport';

// Actions
import { listSellingPointsRequest } from 'store/ducks/sellingPoints/actions';
import { listSellingTerminalsRequest } from 'store/ducks/sellingTerminals/actions';

// Types
import { ApplicationState } from 'store';
import { TrasactionInterface } from 'store/ducks/employeesSales/types';

// Styles
import useStyle from './SellingTransactionNavigation.styles';

interface SellingTransactionNavigationProps {
  setTableReference: (arg: any) => void;
  setTableCSV: (arg: any) => void;
  groupings: Array<string>;
  transactionsList?: TrasactionInterface[];
  fieldFormatting?: Map<string, (arg: any) => any>;
  groupColumns?: {
    key: string;
    name: string;
  }[];
  setTransactionOrder?: (order: Order) => void;
  setTransactionOrderBy?: (orderBy: string) => void;
}

type Props = SellingTransactionNavigationProps;

const SellingTransactionNavigation: React.FC<Props> = ({
  setTableReference,
  setTableCSV,
  groupings,
  transactionsList,
  fieldFormatting,
  groupColumns,
  setTransactionOrder,
  setTransactionOrderBy,
}) => {
  const classes = useStyle();
  const dispatch = useDispatch();
  const hasKey = ['sellingPointId', 'sellingTerminalId', 'responsible'];
  const parentRef: any = useRef();
  const { events } = useDraggable(parentRef);

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('amount');
  const [tableColumns, setTableColumns] = useState<
    Array<{ key: string; name: string }>
  >([]);

  const sellingPointList = useSelector(
    (state: ApplicationState) => state.sellingPoints.sellingPointsList,
  );

  const sellingTerminalList = useSelector(
    (state: ApplicationState) => state.sellingTerminals.sellingTerminalsList,
  );

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  // data formatting
  const initialTransactionsList = useSelector((state: ApplicationState) => {
    const list =
      transactionsList && transactionsList.length
        ? transactionsList
        : state.sellingTerminals.transactionsList || [];
    if (list.length === 0) return [];

    // if there is no kind of grouping
    if (groupings.length === 0) {
      return list.map((t) => ({
        id: t.id && t.id.split('-')[0],
        sellingPointId: t.sellingPointId,
        sellingTerminalId: t.sellingTerminalId,
        amount: t.amount,
        paymentValue: t.paymentValue,
        paymentType: t.paymentType,
        partType: t.type,
        date: t.createdAt,
        responsible: t.SellingPoint && t.SellingPoint.name,
        shortId: t.shortId || '---',
        walletChargeUserName: t.walletChargeUserName,
        walletChargeUserCPF: t?.walletChargeUserCPF || t?.tokenInfo?.document,
        description: t?.description || '',
        tokenStatus:
          t.type === CONST_SELLING_POINTS_OBJECT.TRANSACTION_TYPES.RETURN.KEY
            ? CONST_TOKEN.REVERSED.VALUE
            : t.Token
            ? CONST_TOKEN.STATUS.find((e) => e.KEY === t.Token.status)?.VALUE
            : '--',
        type:
          t.type || t.type === 0
            ? CONST_SELLING_POINTS.TRANSACTION_TYPES.find(
                (e) => e.KEY === t.type,
              )?.VALUE
            : '',
        qrcodeType: t.qrcodeType,
        deviceModel: t.deviceModel,
        transportOperatorId: t.transportOperatorId,
        sellingTerminalName: t.sellingTerminalName,
        sellingTerminalPlace: t.sellingTerminalPlace,
      }));
    }
    // if there is any kind of grouping
    return list.map(
      ({
        count,
        totalValue,
        usedDate,
        type,
        amount,
        paymentValue,
        sellingPointId,
        sellingTerminalId,
        qrcodeType,
        paymentType,
        totalPaymentValue,
      }) => {
        const ret = {
          count,
          totalValue,
        };
        if (usedDate !== undefined) {
          Object.assign(ret, ret, { usedDate });

          const [day, month, year] = usedDate.split('/').map(Number);
          const dateObj = new Date(year, month - 1, day);
          const timestamp = isNaN(dateObj.getTime()) ? 0 : dateObj.getTime();

          Object.assign(ret, ret, { usedDateSort: timestamp });
        }

        if (type !== undefined) {
          const newType = Capitalize(
            CONST_SELLING_POINTS.TRANSACTION_TYPES.find((e) => e.KEY === type)
              ?.VALUE || '--',
          );

          Object.assign(ret, ret, {
            type: `${newType}`,
          });
        }

        if (amount !== undefined) {
          const newType = `${amount}`;
          Object.assign(ret, ret, { amount: newType });
        }
        if (paymentValue !== undefined) {
          const newType = `${paymentValue}`;
          Object.assign(ret, ret, { paymentValue: newType });
        }
        if (totalPaymentValue !== undefined) {
          const newType = `${totalPaymentValue}`;
          Object.assign(ret, ret, { totalPaymentValue: newType });
        }
        if (sellingPointId) {
          Object.assign(ret, ret, { sellingPointId });
        }

        if (sellingTerminalId) {
          Object.assign(ret, ret, { sellingTerminalId });
        }

        if (qrcodeType !== undefined) {
          Object.assign(ret, ret, { qrcodeType });
        }

        if (paymentType !== undefined) {
          Object.assign(ret, ret, { paymentType });
        }

        return ret;
      },
    );
  });

  const handleRequestSort = useCallback(
    (event: React.MouseEvent<unknown>, property: string) => {
      const actualOrderBy = property === 'usedDate' ? 'usedDateSort' : property;

      const isAsc = orderBy === actualOrderBy && order === 'asc';
      const newOrder = isAsc ? 'desc' : 'asc';
      setOrder(newOrder);
      setOrderBy(actualOrderBy);
      if (setTransactionOrder) setTransactionOrder(newOrder);
      if (setTransactionOrderBy) setTransactionOrderBy(actualOrderBy);
    },
    [order, orderBy, setTransactionOrder, setTransactionOrderBy],
  );
  const getSellingName = (transaction: any, key: string): string => {
    const sellingPointName = sellingPointList.find(
      ({ id }) => id === transaction.sellingPointId,
    );

    const sellingTerminalName = sellingTerminalList.find(
      ({ id }) => id === transaction.sellingTerminalId,
    );

    if (key === 'sellingPointId') return sellingPointName?.name || '';
    if (key === 'sellingTerminalId') return sellingTerminalName?.name || '';
    return sellingPointName?.name || sellingTerminalName?.name || '';
  };

  useEffect(() => {
    if (!sellingPointList.length) {
      dispatch(listSellingPointsRequest({ body: { _limit: 9999 } }));
    }
  }, []);

  useEffect(() => {
    if (!sellingTerminalList.length) {
      dispatch(listSellingTerminalsRequest({ body: { _limit: 9999 } }));
    }
  }, []);

  // defining table structure
  useEffect(() => {
    if (groupings.length === 0) {
      setTableColumns([
        { key: 'id', name: 'Código da Transação' },
        { key: 'amount', name: 'Valor' },
        { key: 'paymentValue', name: 'Valor Transação' },
        { key: 'qrcodeType', name: 'QRCode' },
        { key: 'deviceModel', name: 'Dispositivo' },
        { key: 'date', name: 'Data e Hora' },
        { key: 'type', name: 'Tipo de Crédito' },
        { key: 'paymentType', name: 'Forma de Pagamento' },
        { key: 'responsible', name: 'Responsável' },
        { key: 'shortId', name: 'Indentificador' },
        { key: 'walletChargeUserName', name: 'Usuário' },
        { key: 'document', name: 'Documento' },
        { key: 'sellingTerminalPlace', name: 'Guichê' },
        { key: 'description', name: 'Descrição' },
        { key: 'sellingTerminalName', name: 'Rede de venda' },
      ]);
    } else if (groupColumns) setTableColumns(groupColumns);
  }, [groupings, groupColumns]);

  // generating the table csv
  useEffect(() => {
    let csv = '';
    if (tableColumns.length > 0 && initialTransactionsList.length > 0) {
      csv = tableColumns.reduce((a, c) => `${a}${a ? ';' : ''}${c.name}`, '');
      if (transportOperatorSelected.type === 1) csv += ';Operação';

      stableSort(
        initialTransactionsList,
        getComparator(order, orderBy),
      ).forEach((t: { [x: string]: any }) => {
        csv += '\n';
        tableColumns.forEach((c) => {
          const formatter = fieldFormatting && fieldFormatting.get(c.key);

          if (c.key === 'amount' || c.key === 'paymentValue') {
            csv += `${currencyFormat({ value: Number(t[c.key]) || 0 })};`;
          } else if (c.key === 'paymentType' && t.paymentType === 'MULTI') {
            csv += `${
              CONST_TRANSPORT_VOUCHER_COLLECTION.PAYMENT_TYPE.find(
                (e) => e.KEY === t.partType,
              )?.VALUE
            };`;
          } else if (hasKey.includes(c.key)) {
            csv += `${getSellingName(t, c.key)};`;
          } else if (c.key === 'document') {
            csv += `${maskCPF(t.walletChargeUserCPF) || '--'};`;
          } else if (formatter) {
            csv += `${formatter(t[c.key] || '--')};`;
          } else {
            csv += `${t[c.key] || '--'};`;
          }
        });
        if (transportOperatorSelected.type === 1) {
          csv +=
            transportOperatorsList.find((e) => e.id === t?.transportOperatorId)
              ?.name || '---';
        }
      });
    }
    setTableCSV(csv);
    // eslint-disable-next-line
  }, [tableColumns, initialTransactionsList]);

  return (
    <>
      <TableVirtuoso
        style={{
          height: '90vh',
          overflowY: 'hidden',
        }}
        overscan={10}
        useWindowScroll
        data={stableSort(
          initialTransactionsList,
          getComparator(order, orderBy),
        )}
        components={{
          Scroller: React.forwardRef((props, ref) => (
            <TableContainer
              className={classes.table}
              {...props}
              ref={parentRef}
              {...events}
            />
          )),

          Table: (props: any) => (
            <Table {...props} style={{ borderCollapse: 'separate' }} />
          ),
          TableHead,
          TableRow,
          TableBody: React.forwardRef((props, ref) => (
            <TableBody {...props} ref={ref} />
          )),
        }}
        fixedHeaderContent={() => (
          <TableRow>
            {tableColumns.length > 0 &&
              tableColumns.map((col) => {
                const isActive =
                  col.key === 'usedDate'
                    ? orderBy === 'usedDateSort'
                    : orderBy === col.key;

                const sortDirection = isActive ? order : 'asc';

                return (
                  <TableCell
                    key={col.key}
                    variant='head'
                    sortDirection={isActive ? order : false}>
                    <TableSortLabel
                      classes={{
                        root: classes.sortLabel,
                        icon: classes.sortLabel,
                      }}
                      active
                      direction={sortDirection}
                      onClick={(event) => handleRequestSort(event, col.key)}>
                      {col.name}

                      {isActive ? (
                        <span className={classes.visuallyHidden}>
                          {order === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            {transportOperatorSelected.type === 1 && (
              <TableCell
                variant='head'
                align='left'
                sortDirection={
                  orderBy === 'transportOperatorId' ? order : false
                }>
                <TableSortLabel
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}
                  active
                  direction={orderBy === 'transportOperatorId' ? order : 'asc'}
                  onClick={(event) =>
                    handleRequestSort(event, 'transportOperatorId')
                  }>
                  Operação
                  {orderBy === 'transportOperatorId' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            )}
          </TableRow>
        )}
        itemContent={(index: any, transaction: any) => (
          <>
            {tableColumns.map((col) => {
              const formatter = fieldFormatting && fieldFormatting.get(col.key);

              if (
                col.key === 'paymentType' &&
                transaction.paymentType === 'MULTI'
              ) {
                const labelPaymentType = CONST_TRANSPORT_VOUCHER_COLLECTION.PAYMENT_TYPE.find(
                  (item) => item.KEY === transaction.partType,
                )?.VALUE;

                return (
                  <TableCell key={`${transaction.id}-${col.key}`}>
                    {labelPaymentType}
                  </TableCell>
                );
              }

              if (col.key === 'document') {
                return (
                  <TableCell key={`${transaction.id}-${col.key}`}>
                    {maskCPF(transaction.walletChargeUserCPF) || '--'}
                  </TableCell>
                );
              }

              return (
                <TableCell key={`${transaction.id}-${col.key}`}>
                  {formatter && formatter(transaction[col.key])}
                  {hasKey.includes(col.key) &&
                    getSellingName(transaction, col.key)}
                  {!formatter &&
                    !hasKey.includes(col.key) &&
                    transaction[col.key]}
                </TableCell>
              );
            })}

            {transportOperatorSelected.type === 1 && (
              <TableCell>
                {transportOperatorsList.find(
                  (e) => e.id === transaction?.transportOperatorId,
                )?.name || '---'}
              </TableCell>
            )}
          </>
        )}
      />
    </>
  );
};

export { SellingTransactionNavigation };
export default SellingTransactionNavigation;
